import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { Close } from 'react-ionicons';
import { getActiveOrdersGraph } from 'api/orders/api';

interface MobilityOrderTrackingBannerProps {
  className?: string;
}

const MobilityOrderTrackingBanner = ({
  className,
}: MobilityOrderTrackingBannerProps) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(
    ['active-orders-metrics'],
    getActiveOrdersGraph,
  );

  const [visible, setVisible] = useState(false);
  const [storageKey, setStorageKey] = useState<string>('');

  // Compute number of open orders
  const openOrdersCount =
    data?.statuses
      ?.filter((s) => s.status !== 'Complete')
      .reduce((acc, s) => acc + s.count, 0) ?? 0;

  // Banner visibility logic
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem('g360-token-storage');
    if (!raw) return;

    let tokenData: { token?: string } | null = null;
    try {
      tokenData = JSON.parse(raw);
    } catch {
      tokenData = null;
    }

    const encoded = tokenData?.token;
    if (!encoded) return;

    let exp: string | number = 'noexp';
    try {
      const payload = JSON.parse(atob(encoded.split('.')[1]));
      exp = payload.exp;
    } catch {
      exp = 'noexp';
    }

    const key = `mobility-order-tracking-banner-hidden-${exp}`;

    setStorageKey(key);

    const hidden = localStorage.getItem(key);

    setVisible(!hidden && openOrdersCount > 1);
  }, [openOrdersCount]);

  const handleClose = useCallback(() => {
    setVisible(false);
    if (storageKey) localStorage.setItem(storageKey, 'true');
  }, [storageKey]);

  if (isLoading || !visible) return null;

  return (
    <div
      className={clsx(
        'flex flex-col items-end justify-between gap-2 rounded border border-status-info-default p-4 sm:flex-row sm:items-center',
        className,
      )}
    >
      <div className="text-primary-main flex w-full flex-col gap-2 pr-4 text-content-base-default sm:w-auto">
        <div className="font-bold">Coming soon: Mobility order tracking</div>
        <div className="text-sm font-semibold">
          Want to review your mobility order? Check out the order history page.
        </div>
      </div>

      <div className="flex gap-2">
        <GraniteButton
          variant="tertiary"
          size="small"
          className="whitespace-nowrap"
          onClick={() => navigate('/access-express/v3')}
        >
          Go to Order history
        </GraniteButton>

        <GraniteButton
          variant="unstyled"
          size="small"
          className="!px-0 text-content-base-default"
          onClick={handleClose}
        >
          <Close width="24px" height="24px" color="currentColor" />
        </GraniteButton>
      </div>
    </div>
  );
};

export default MobilityOrderTrackingBanner;
